import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';

/** One product-type family of the matching taxonomy. */
export interface ProductTypeFamily {
  id: string;
  nouns: string[];
}

export interface GetProductTypeFamiliesResponse {
  families: ProductTypeFamily[];
}

export interface CreateProductTypeFamilyArgs {
  id: string;
  nouns: string[];
}

export interface UpdateProductTypeFamilyArgs {
  familyId: string;
  nouns: string[];
}

export interface SaveProductTypeFamilyResponse {
  family: ProductTypeFamily;
}

export interface DeleteProductTypeFamilyArgs {
  familyId: string;
}

export interface DeleteProductTypeFamilyResponse {
  deleted: string;
}

/** One catalog product's detected type. noun/family null = the lexicon recognizes no
 *  type in the product's fields — the matcher's type guard is inert for it. */
export interface CatalogTypeCoverageItem {
  productId: string;
  title: string;
  catalogType?: string | null;
  noun?: string | null;
  family?: string | null;
}

export interface CatalogTypeCoverageResponse {
  total: number;
  untyped: number;
  /** Every live catalog product, untyped ones first (server-sorted). */
  items: CatalogTypeCoverageItem[];
}

/**
 * Pure request builders — exported so the exact wire (URL + method + body) is
 * lockable in a `toEqual` unit test. Endpoints (sku-mapping-service
 * `ProductTypeFamiliesController`):
 *   GET    /sku/v1/productTypeFamilies
 *   POST   /sku/v1/productTypeFamilies              body { id, nouns }
 *   PUT    /sku/v1/productTypeFamilies/{familyId}   body { nouns }
 *   DELETE /sku/v1/productTypeFamilies/{familyId}
 * (Reload rides the existing POST /sku/v1/searchSynonyms/reload — one button
 * refreshes every vocabulary cache.)
 */
export const productTypeFamiliesRequestBuilders = {
  getProductTypeFamilies: () => ({
    url: `${services.sku}/v1/productTypeFamilies`,
    method: 'GET' as const,
  }),
  createProductTypeFamily: ({ id, nouns }: CreateProductTypeFamilyArgs) => ({
    url: `${services.sku}/v1/productTypeFamilies`,
    method: 'POST' as const,
    body: { id, nouns },
  }),
  updateProductTypeFamily: ({ familyId, nouns }: UpdateProductTypeFamilyArgs) => ({
    url: `${services.sku}/v1/productTypeFamilies/${encodeURIComponent(familyId)}`,
    method: 'PUT' as const,
    body: { nouns },
  }),
  deleteProductTypeFamily: ({ familyId }: DeleteProductTypeFamilyArgs) => ({
    url: `${services.sku}/v1/productTypeFamilies/${encodeURIComponent(familyId)}`,
    method: 'DELETE' as const,
  }),
  checkCatalogTypeCoverage: () => ({
    url: `${services.sku}/v1/productTypeFamilies/catalogCoverage`,
    method: 'GET' as const,
  }),
};

/** Tolerate a defensive `{ data: {...} }` wrapper (other portal services ship both). */
function unwrap<T>(response: T | { data?: T } | unknown): T | undefined {
  const r = response as { data?: T };
  if (r && typeof r === 'object' && 'data' in r && r.data != null) return r.data;
  return response as T;
}

export function unwrapFamilies(response: unknown): GetProductTypeFamiliesResponse {
  const d = unwrap<GetProductTypeFamiliesResponse>(response);
  return { families: Array.isArray(d?.families) ? d!.families : [] };
}

export function unwrapSavedFamily(response: unknown): SaveProductTypeFamilyResponse {
  const d = unwrap<SaveProductTypeFamilyResponse>(response);
  const family = (d?.family ?? {}) as ProductTypeFamily;
  return {
    family: {
      id: family.id ?? '',
      nouns: Array.isArray(family.nouns) ? family.nouns : [],
    },
  };
}

export function unwrapDeletedFamily(response: unknown): DeleteProductTypeFamilyResponse {
  const d = unwrap<DeleteProductTypeFamilyResponse>(response);
  return { deleted: d?.deleted ?? '' };
}

export function unwrapCatalogTypeCoverage(response: unknown): CatalogTypeCoverageResponse {
  const d = unwrap<CatalogTypeCoverageResponse>(response);
  return {
    total: d?.total ?? 0,
    untyped: d?.untyped ?? 0,
    items: Array.isArray(d?.items) ? d!.items : [],
  };
}

/**
 * Product-type taxonomy slice (super-admin surface, flag
 * `omProd722AiCatalogMappingAssistant`): the families/nouns behind head-noun
 * typing, the family guard and the absent-type gate. Every write reloads the
 * service-side caches, so edits apply to the next matching run without a deploy.
 */
export const productTypeFamiliesApi = createApi({
  reducerPath: 'productTypeFamiliesApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['ProductTypeFamilies'],
  endpoints: (builder) => ({
    // GET /sku/v1/productTypeFamilies — the whole taxonomy (~11 families, unpaged).
    getProductTypeFamilies: builder.query<GetProductTypeFamiliesResponse, void>({
      query: productTypeFamiliesRequestBuilders.getProductTypeFamilies,
      transformResponse: unwrapFamilies,
      providesTags: [{ type: 'ProductTypeFamilies', id: 'LIST' }],
    }),
    // POST — 409s carry the duplicate/collision detail (a noun or its plural-folded
    // form may belong to exactly one family).
    createProductTypeFamily: builder.mutation<SaveProductTypeFamilyResponse, CreateProductTypeFamilyArgs>({
      query: productTypeFamiliesRequestBuilders.createProductTypeFamily,
      transformResponse: unwrapSavedFamily,
      invalidatesTags: [{ type: 'ProductTypeFamilies', id: 'LIST' }],
    }),
    // PUT /{familyId} — the family name is immutable (delete + recreate to rename).
    updateProductTypeFamily: builder.mutation<SaveProductTypeFamilyResponse, UpdateProductTypeFamilyArgs>({
      query: productTypeFamiliesRequestBuilders.updateProductTypeFamily,
      transformResponse: unwrapSavedFamily,
      invalidatesTags: [{ type: 'ProductTypeFamilies', id: 'LIST' }],
    }),
    // DELETE /{familyId} — rows of these types simply degrade to untyped (fail-open).
    deleteProductTypeFamily: builder.mutation<DeleteProductTypeFamilyResponse, DeleteProductTypeFamilyArgs>({
      query: productTypeFamiliesRequestBuilders.deleteProductTypeFamily,
      transformResponse: unwrapDeletedFamily,
      invalidatesTags: [{ type: 'ProductTypeFamilies', id: 'LIST' }],
    }),
    // GET /catalogCoverage — the matcher's type detection over every live catalog
    // product; on-demand (lazy) and never cached, so it always reflects the taxonomy
    // as just edited.
    checkCatalogTypeCoverage: builder.query<CatalogTypeCoverageResponse, void>({
      query: productTypeFamiliesRequestBuilders.checkCatalogTypeCoverage,
      transformResponse: unwrapCatalogTypeCoverage,
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetProductTypeFamiliesQuery,
  useCreateProductTypeFamilyMutation,
  useUpdateProductTypeFamilyMutation,
  useDeleteProductTypeFamilyMutation,
  useLazyCheckCatalogTypeCoverageQuery,
} = productTypeFamiliesApi;
